import React, { useMemo } from 'react'
import { Layout, Menu, Button, Tag, Typography, Result } from 'antd'
import {
  PlusOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  LockOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import RecruitmentSetup from './RecruitmentSetup'
import RecruitmentList from './RecruitmentList'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import AllModels from '../components/AllModels'
import MatchedModels from '../components/MatchedModels'

const { Sider, Content } = Layout

const StyledLayout = styled(Layout)`
  min-height: 100vh;
`

const StyledSider = styled(Sider)`
  background: #fff;
  @media (max-width: 768px) {
    display: none;
  }
`

const StyledMenu = styled(Menu)`
  height: 100%;
  border-right: 0;
`

const MobileHeader = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`

const MobileTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`

const ScrollableMenu = styled.div`
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  background-color: #fcfcfc;
  padding: 10px 5px;
  border-radius: 10px 10px 0 0;

  &::-webkit-scrollbar {
    display: none;
  }
`

const MenuItem = styled.div`
  display: inline-block;
  padding: 10px 15px;
  cursor: pointer;
  color: ${(props) => (props.active ? '#d5728a' : 'rgba(0, 0, 0, 0.65)')};
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  position: relative;
  transition: all 0.3s;

  &:hover {
    color: #d5728a;
  }

  .icon {
    margin-right: 8px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #d5728a;
    transform: scaleX(${(props) => (props.active ? 1 : 0)});
    transform-origin: ${(props) => (props.active ? 'left' : 'right')};
    transition: transform 0.3s ease-in-out;
  }
`

const StyledButton = styled(Button)``

const UpgradeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 100;
  padding: 20px;
  text-align: center;
  border-radius: 8px;
`

const UpgradeButton = styled(Button)`
  background: linear-gradient(135deg, #d5728a, #c45f77);
  border: none;
  color: white;
  margin-top: 16px;
  &:hover {
    background: linear-gradient(135deg, #c45f77, #b54d65);
    color: white;
  }
`

const StyledContent = styled(Content)`
  background: transparent;
  position: relative;
  min-height: 80vh;
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 80vh;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
`

const RecruitmentDashboard = () => {
  const navigate = useNavigate()
  const { tab } = useParams()
  const { businessId, user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const hasRecruitmentAccess = useMemo(
    () => hasFeature('recruitment'),
    [hasFeature]
  )

  const menuItems = [
    {
      key: 'add',
      label: '新增招聘',
      icon: <PlusOutlined />
    },
    {
      key: 'recruitmentInfo',
      label: '店家招聘信息',
      icon: <FileTextOutlined />
    },
    {
      key: 'models',
      label: '在線模特兒',
      icon: <TeamOutlined />
    },
    {
      key: 'completedCases',
      label: '已媒合案件',
      icon: <CheckCircleOutlined />
    }
  ]

  const handleMenuClick = (e) => {
    navigate(`/recruitment-dashboard/${e.key}`)
  }

  const handleAddRecruitment = () => {
    navigate('/recruitment-dashboard/add')
  }

  const renderContent = () => {
    switch (tab) {
      case 'recruitmentInfo':
        return (
          <ContentWrapper>
            <RecruitmentList businessId={businessId} />
          </ContentWrapper>
        )
      case 'models':
        return (
          <ContentWrapper>
            <AllModels />
          </ContentWrapper>
        )
      case 'completedCases':
        return (
          <ContentWrapper>
            <MatchedModels />
          </ContentWrapper>
        )
      case 'add':
        return (
          <ContentWrapper>
            <RecruitmentSetup />
          </ContentWrapper>
        )
      default:
        return (
          <ContentWrapper>
            <RecruitmentList businessId={businessId} />
          </ContentWrapper>
        )
    }
  }

  if (!permissionsLoaded) {
    return (
      <StyledLayout>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Typography.Text type='secondary'>正在載入權限...</Typography.Text>
        </div>
      </StyledLayout>
    )
  }

  if (!hasRecruitmentAccess) {
    return (
      <StyledLayout>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
            width: '100%'
          }}
        >
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能僅限品牌方案用戶使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </div>
      </StyledLayout>
    )
  }

  return (
    <StyledLayout>
      <ScrollableMenu>
        {menuItems.map((item) => (
          <MenuItem
            key={item.key}
            active={tab === item.key}
            onClick={() => handleMenuClick({ key: item.key })}
          >
            {React.cloneElement(item.icon, { className: 'icon' })}
            {item.label}
          </MenuItem>
        ))}
      </ScrollableMenu>
      <Layout>
        <StyledContent>{renderContent()}</StyledContent>
      </Layout>
    </StyledLayout>
  )
}

export default RecruitmentDashboard
